<script lang="ts">
  /**
   * A destructive action that asks once before it happens.
   *
   * The first click arms the question and starts a short countdown, so an
   * accidental click does not erase anything and the question goes away on its
   * own if it is never answered.
   */
  interface Props {
    /** The label while the question is not asked. */
    label: string;
    /** The label while it is, and what a second click will do. */
    confirmLabel: string;
    /** What the action does, for the tooltip. */
    title?: string;
    /**
     * `quiet` unobtrusively greys the button out, `danger` keeps the ordinary
     * button look and only turns red when the question is armed.
     */
    variant?: 'quiet' | 'danger';
    onconfirm: () => void;
  }

  let { label, confirmLabel, title, variant = 'danger', onconfirm }: Props = $props();

  /** How long the question stays open before taking itself back. */
  const CONFIRM_MS = 4000;

  let armed = $state(false);
  let timer: ReturnType<typeof setTimeout> | null = null;

  function stopCountdown(): void {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  }

  function click(): void {
    if (!armed) {
      armed = true;
      stopCountdown();
      timer = setTimeout(() => {
        timer = null;
        armed = false;
      }, CONFIRM_MS);
      return;
    }
    stopCountdown();
    armed = false;
    onconfirm();
  }

  // A question left behind by a closed screen must not outlive it.
  $effect(() => stopCountdown);
</script>

<button
  {title}
  class={variant}
  class:confirming={armed}
  onclick={click}
>
  {armed ? confirmLabel : label}
</button>

<style>
  button.quiet {
    background: none;
    border-color: transparent;
    color: var(--text-faint);
    font-size: 12.5px;
    padding: 4px 8px;
  }

  button.quiet:hover,
  button.quiet.confirming,
  button.danger:hover,
  button.danger.confirming {
    color: var(--fail);
    border-color: var(--fail);
  }
</style>
